"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import {
  getDefaultUnsavedConfirmMessage,
  hasAnyUnsavedChanges,
} from "@/lib/navigation";
import { coerceLocale } from "@/i18n/locales";
import { useNavigationStore } from "@/store/navigation/navigation-store";

export default function LocaleClientEffects() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const endNavigation = useNavigationStore((state) => state.endNavigation);

  useEffect(() => {
    const normalizedLocale = coerceLocale(locale);
    document.documentElement.lang = normalizedLocale;
    document.documentElement.dir = normalizedLocale === "ar" ? "rtl" : "ltr";
  }, [locale]);

  useEffect(() => {
    endNavigation();
  }, [endNavigation, pathname, searchParams]);

  useEffect(() => {
    const currentPathname = pathname || window.location.pathname;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasAnyUnsavedChanges(currentPathname)) return;
      event.preventDefault();
      event.returnValue = "";
    };

    const handleAnchorNavigation = (event: MouseEvent) => {
      if (!hasAnyUnsavedChanges(currentPathname)) return;

      const target = event.target as HTMLElement | null;
      const anchor = target?.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.dataset.skipUnsavedCheck === "true") return;
      if (anchor.target === "_blank") return;
      if (anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        url.hash === window.location.hash
      ) {
        return;
      }

      const shouldContinue = window.confirm(getDefaultUnsavedConfirmMessage());
      if (!shouldContinue) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handlePopState = () => {
      if (!hasAnyUnsavedChanges(currentPathname)) return;
      const shouldContinue = window.confirm(getDefaultUnsavedConfirmMessage());
      if (!shouldContinue) {
        window.history.go(1);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleAnchorNavigation, true);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleAnchorNavigation, true);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [pathname]);

  return null;
}
